import { useMatchList } from "@/api/services/admin/match";
import { TGetMatchTypeDto } from "@/api/services/admin/match/dtos/get-match-list.dto";
import { useEnum } from "@/api/services/global/enum";
import { Table, TableSorting } from "@/components/elements/Table";
import { EMPTY_STR } from "@/constants/string";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { EMatchContractStatus } from "@/types/enum";
import { truncateWithEllipsis } from "@/utils/string";
import { getDynamicRoute } from "@/utils/url";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { DATE_FORMAT } from "@/constants/datetime";
import { NewPagination } from "@/components/elements/Pagination/NewPagination";
import { Box } from "@mui/material";

interface MatchTableProps {
  contractStatuses: EMatchContractStatus[];
}

export const MatchTable: FC<MatchTableProps> = ({ contractStatuses }) => {
  const { data: matchContractStatuses } = useEnum("EMatchContractStatus");
  const navigate = useNavigate();
  const getValueFollowTypeTable = (original: TGetMatchTypeDto) => {
    const path = window.location.pathname;

    const rules = [
      [AppRoutes.duringContractList, original.contractStartDate],
      [AppRoutes.canceledContractList, original.contractEndDate],
      [AppRoutes.trialCancelledList, original.trialEndDate],
      [AppRoutes.trialMatchList, original.trialStartDate],
    ] as const;

    for (const [key, value] of rules) {
      if (path.includes(key)) {
        return value;
      }
    }
  };
  const getLabelFollowTypeTable = () => {
    const path = window.location.pathname;

    const rules = [
      [AppRoutes.duringContractList, "契約開始日"],
      [AppRoutes.canceledContractList, "解約日"],
      [AppRoutes.trialCancelledList, "トライアル終了日"],
      [AppRoutes.trialMatchList, "トライアル開始日"],
    ] as const;

    for (const [key, value] of rules) {
      if (path.includes(key)) {
        return value;
      }
    }
    return "";
  };
  const columns = useMemo<MRT_ColumnDef<TGetMatchTypeDto>[]>(
    () => [
      {
        header: "許可番号",
        id: "matchLicenseNumber",
        accessorKey: "matchLicenseNumber",
        Cell: ({ row }) => {
          return <Box sx={{ pl: 2 }}>{row.original.matchLicenseNumber}</Box>;
        },
        Header: (
          <Box sx={{ pl: 2 }}>
            <TableSorting name="match.matchLicenseNumber">許可番号</TableSorting>
          </Box>
        ),
        size: 120,
      },
      {
        header: "事業者名",
        id: "matchName",
        accessorKey: "matchName",
        Header: <TableSorting name="match.matchName">事業者名</TableSorting>,
        Cell: ({ row }) => truncateWithEllipsis(row.original.matchName, 50),
        maxSize: 378,
        size: 378,
      },
      {
        header: "求人数",
        Cell: ({ row }) => row.original.jobCount ?? 0,
        muiTableHeadCellProps: {
          sx: {
            maxWidth: 80,
            minWidth: 20,
          },
        },
        muiTableBodyCellProps: {
          sx: {
            maxWidth: 80,
            minWidth: 20,
          },
        },
      },
      {
        header: "人材数",
        Cell: ({ row }) => row.original.talentCount ?? 0,
        muiTableHeadCellProps: {
          sx: {
            maxWidth: 80,
            minWidth: 20,
          },
        },
        muiTableBodyCellProps: {
          sx: {
            maxWidth: 80,
            minWidth: 20,
          },
        },
      },
      {
        header: "案件数",
        Cell: ({ row }) => row.original.caseCount ?? 0,
        muiTableHeadCellProps: {
          sx: {
            maxWidth: 80,
            minWidth: 20,
          },
        },
        muiTableBodyCellProps: {
          sx: {
            maxWidth: 80,
            minWidth: 20,
          },
        },
      },
      {
        // 括弧は全角 (契約プラン文字列と同一表記)。
        // MRT は size (既定 150px) を th の inline style に出力し sx の width を上書きするため、
        // 幅は size で指定する。ラベル7文字 + 左右パディング 16px で約 130px 必要。
        header: "面接数（累計）",
        Cell: ({ row }) => row.original.interviewCount ?? 0,
        size: 130,
      },
      {
        header: "課金対象数",
        Cell: ({ row }) => {
          return row.original.billingCount ?? 0;
        },
        size: 20,
      },
      {
        header: "前月課金数",
        Cell: ({ row }) => {
          return row.original.prevMonthBillingCount ?? 0;
        },
        size: 20,
      },
      {
        header: getLabelFollowTypeTable(),
        Cell: ({ row }) => {
          const { original } = row;
          const valueFollowTypeTable = getValueFollowTypeTable(original);
          if (!valueFollowTypeTable) {
            return EMPTY_STR.DATE;
          }
          const date = dayjs(valueFollowTypeTable);
          return date.isValid() ? date.format(DATE_FORMAT) : EMPTY_STR.DATE;
        },
        Header: <TableSorting name="match.contractStartDate">{getLabelFollowTypeTable()}</TableSorting>,
        size: 20,
      },
      {
        // 一覧の最終列。ラベル8文字が最も広いため size:20 では truncate される。
        header: "最終ログイン日時",
        Cell: ({ row }) => {
          const { lastLoginTime } = row.original;
          // dayjs(null) は現在時刻を返すため falsy ガードが必須
          if (!lastLoginTime) {
            return EMPTY_STR.DATE;
          }
          const date = dayjs(lastLoginTime);
          return date.isValid() ? date.format(DATE_FORMAT) : EMPTY_STR.DATE;
        },
        size: 140,
      },
    ],
    [matchContractStatuses],
  );

  const { data, isLoading } = useMatchList(contractStatuses);
  const [heightTable] = useState<string>("calc(100vh - 430px)");

  const { table } = useTable({
    columns,
    rows: data?.data ?? [],
    onClickRow: (row) => navigate(getDynamicRoute(AppRoutes.matchDetail, { id: row.id })),
    pinningColumns: ["action"],
    renderEmptyRowsFallback: ({}) => <span></span>,
    muiTableBodyRowProps: ({ row }) => ({
      className: row.original.deletedAt ? "deleted" : "",
    }),
  });

  return (
    <>
      <Table table={table} isLoading={isLoading} height={heightTable} minHeight={150} />
      <Box mt={1}>
        <NewPagination totalPage={data?.pagination.totalPage} />
      </Box>
    </>
  );
};
